import { getViemClients, } from "@bracket/contractor-next";
import { assertAllowance, } from "@bracket/contractor-next/erc20";
import { nanoid, } from "nanoid";
import bytes32 from "bytes32";
import type { Config, } from "@wagmi/core";

import {
	bigintize,
	getContractWithABI,
	getTokenDefinition,
	isBlockHash,
	type BlockHash,
	type TokenIdentifier,
	type ValidChainId,
} from "@bracket/chainography";

export declare type BurnProps = {
	amount: number|bigint;
	minColAmount: bigint;
	token: TokenIdentifier;
	salt?: string;
	chainId?: ValidChainId;
	attempt?: number;
	config?: Config;
};



/**
 * Burn brktETH in exchange for a collateral token of equal value.
 * @param {TokenIdentifier} token the token we're burning to.
 * @param {bigint|number} amount the amount of brkt being commited to this burn
 * @param {bigint} minColAmount this amount of colateral being redeemed
 * @param {Config} config the wagmi config, if using it.
 * @param {ValidChainId} chainId , default 1
 * @param {string} salt a unique specifier of MANDTORY 32 chars, you can hold on to if needed for a UID
 * @param param0 
 * @returns 
 */
export const burn = async ({
	token,
	amount,
	minColAmount,
	config,
	salt,
	chainId = 1,
}: BurnProps): Promise<BlockHash> => {

	try {

		if(chainId !== 1)
			throw new Error("mint supports only chain 1");

		const [client, wclient] = await getViemClients({ chainId, config, });
		const tok = getTokenDefinition(token);
		const [address, abi] = getContractWithABI("BrktETH", chainId);
		const brktAmount = typeof amount === "bigint" ? amount : bigintize(amount, token);
		salt = bytes32({ input: salt || nanoid(32), });

		await assertAllowance({
			amount: brktAmount,
			spender: address,
			token: tok,
			config,
		});

		const {
			request,
			result,
		} = await client.simulateContract({
			address,
			abi,
			functionName: "burn",
			args: [
				tok.address,
				brktAmount,
				minColAmount,
				salt
			],
			account: wclient.account.address
		});

		if(result) {
			const hash = await wclient.writeContract(request);
			if(isBlockHash(hash)) {
				await client.waitForTransactionReceipt({ hash, });
				return hash;
			}
			throw new Error("did not receive a blockhash");
		}
		throw new Error("mint transaction failed");

	}
	catch (err) {

		throw new Error(`@bracket/chainography/brktETH:burn failed - ${err.message}`);
	}
};
